import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { geoMercator, geoPath } from "d3-geo";
import { eur, num, showMoney } from "@/lib/format";
import { Loader2 } from "lucide-react";

const W = 420;
const H = 520;
const ALIAS = { aosta: "valledaosta" };
const key = (s) => {
  const k = (s || "").split("/")[0].toLowerCase().replace(/\s(e della|e|nell'|di)\s/g, " ").normalize("NFD").replace(/[^a-z]/g, "");
  return ALIAS[k] || k;
};

export const ItalyMap = ({ level, items, by, onPick }) => {
  const [hover, setHover] = useState(null);
  const file = level === "regioni" ? "regions" : "provinces";
  const field = level === "regioni" ? "reg_name" : "prov_name";
  const { data: geo } = useQuery({ queryKey: ["geo", file], staleTime: Infinity,
    queryFn: async () => (await fetch(`${process.env.PUBLIC_URL || ""}/geo/${file}.min.json`)).json() });
  const byKey = useMemo(() => Object.fromEntries(items.map((i) => [key(i.name), i])), [items]);
  const path = useMemo(() => (geo ? geoPath(geoMercator().fitSize([W, H], geo)) : null), [geo]);
  if (!geo) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  const max = Math.max(...items.map((i) => i[by] || 0), 1);
  return (
    <div className="relative" data-testid={`italy-map-${level}`}>
      <svg viewBox={`0 0 ${W} ${H}`} className="mx-auto h-auto w-full max-w-[460px]" onMouseLeave={() => setHover(null)}>
        {geo.features.map((f) => {
          const it = byKey[key(f.properties[field])];
          const t = it ? Math.sqrt((it[by] || 0) / max) : 0;
          return (
            <path key={f.properties[field]} d={path(f)} stroke="hsl(var(--background))" strokeWidth={0.6}
              fill={it ? `hsl(var(--primary) / ${0.12 + t * 0.88})` : "hsl(var(--muted))"}
              className={it ? "cursor-pointer transition-opacity hover:opacity-80" : ""}
              onMouseMove={(e) => setHover({ name: it?.name || f.properties[field].split("/")[0], it, x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY })}
              onClick={() => it && onPick(it.name)} data-testid={`map-area-${key(f.properties[field])}`} />
          );
        })}
      </svg>
      {hover && (
        <div className="pointer-events-none absolute z-10 rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs shadow" style={{ left: hover.x + 12, top: hover.y + 12 }}>
          <p className="font-semibold">{hover.name}</p>
          {hover.it ? <p className="text-muted-foreground">{num(hover.it.value)} clienti{showMoney() && hover.it.revenue != null ? ` · ${eur(hover.it.revenue)}` : ""}</p> : <p className="text-muted-foreground">Nessuna cliente</p>}
        </div>
      )}
      <div className="mt-2 flex items-center justify-center gap-2 text-[11px] text-muted-foreground">
        <span>meno</span><div className="h-2 w-32 rounded" style={{ background: "linear-gradient(to right, hsl(var(--primary) / 0.12), hsl(var(--primary)))" }} /><span>più {by === "revenue" ? "fatturato" : "clienti"}</span>
      </div>
    </div>
  );
};
